/**
 * Accepting a team invite.
 *
 * PUBLIC, and it has to be: the person has no account access yet, which is the
 * whole point. The token in the URL is the credential, so this page asks for a
 * password and nothing else — no email to confirm, no old password to prove.
 *
 * On success the server has already set the session cookie, so the app is
 * entered with a hard navigation rather than a client-side one. A full reload
 * is the cheapest way to guarantee every cached query starts from the new
 * identity instead of the anonymous one this page was loaded with.
 */
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Eye, EyeOff, Lock } from 'lucide-react';
import { useParams } from 'react-router-dom';

import './auth.css';

import { AlphaMark } from '@/components/app/Sidebar';
import { ThemeToggle } from '@/components/app/ThemeToggle';
import { Button, Callout, Field, Input } from '@/components/ui';
import { request } from '@/lib/api';
import { ROUTES } from '@/lib/constants';

const MIN_LENGTH = 12;

type Probe =
  | { state: 'loading' }
  | { state: 'ok'; email: string; name: string; expiresAt: string }
  | { state: 'bad'; reason: string };

interface ProbeResponse {
  ok: boolean;
  reason?: string;
  email?: string;
  name?: string;
  expiresAt?: string;
}

/** Plain language for each way a link can fail. */
function explain(reason: string): { title: string; detail: string } {
  switch (reason) {
    case 'expired':
      return {
        title: 'This link has expired',
        detail: 'Invite links are deliberately short-lived. Ask whoever invited you to send a new one — it takes them a click.',
      };
    case 'accepted':
      return {
        title: 'This link has already been used',
        detail: 'Your password is set. Sign in with your email address instead.',
      };
    case 'revoked':
      return {
        title: 'This link is no longer valid',
        detail: 'A newer invite replaced it, or the account was deactivated. Check for a more recent email, or ask for another.',
      };
    default:
      return {
        title: 'This link is not valid',
        detail: 'It may have been copied incompletely. Try opening it straight from the email, or ask for a new one.',
      };
  }
}

export function InvitePage() {
  const { token = '' } = useParams<{ token: string }>();
  const [probe, setProbe] = useState<Probe>({ state: 'loading' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [revealed, setRevealed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void (async () => {
      try {
        const res = await request<ProbeResponse>(`/auth/invite/${encodeURIComponent(token)}`);
        if (!live) return;
        setProbe(
          res.ok
            ? { state: 'ok', email: res.email ?? '', name: res.name ?? '', expiresAt: res.expiresAt ?? '' }
            : { state: 'bad', reason: res.reason ?? 'unknown' },
        );
      } catch {
        if (live) setProbe({ state: 'bad', reason: 'unknown' });
      }
    })();
    return () => {
      live = false;
    };
  }, [token]);

  const tooShort = password.length > 0 && password.length < MIN_LENGTH;
  const mismatch = confirm.length > 0 && confirm !== password;
  const canSubmit = password.length >= MIN_LENGTH && confirm === password && !submitting;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await request(`/auth/invite/${encodeURIComponent(token)}`, { method: 'POST', body: { password } });
      window.location.assign(ROUTES.inbox);
    } catch (err) {
      setSubmitting(false);
      setError(err instanceof Error ? err.message : 'That did not work. Try again.');
    }
  }

  return (
    <div className="af-auth flex h-full w-full flex-col bg-[var(--surface-1)]">
      <header className="flex h-[var(--h-topbar)] shrink-0 items-center px-6">
        <div className="mx-auto flex w-full max-w-[var(--w-auth-form)] items-center justify-between gap-3">
          <span className="flex items-center gap-2">
            <AlphaMark />
            <span className="flex flex-col leading-none">
              <span className="text-base font-semibold tracking-[var(--tracking-tight)] text-primary">AlphaForm</span>
              <span className="eyebrow mt-1">CRM</span>
            </span>
          </span>
          <ThemeToggle />
        </div>
      </header>

      <main className="flex min-h-0 flex-1 items-center justify-center overflow-y-auto px-6">
        <div className="w-full max-w-[var(--w-auth-form)] py-10">
          {probe.state === 'loading' && <p className="text-sm text-secondary">Checking your link…</p>}

          {probe.state === 'bad' && (
            <>
              <h1 className="text-xl font-semibold text-primary">{explain(probe.reason).title}</h1>
              <p className="mt-2 text-sm text-secondary">{explain(probe.reason).detail}</p>
              <Button className="mt-6" onClick={() => window.location.assign(ROUTES.login)}>
                Go to sign in
              </Button>
            </>
          )}

          {probe.state === 'ok' && (
            <form onSubmit={onSubmit} noValidate>
              <h1 className="text-xl font-semibold text-primary">Choose a password</h1>
              <p className="mt-2 text-sm text-secondary">
                You are setting up <span className="font-medium text-primary">{probe.email}</span>. This link works once,
                so finish now — you will be signed in straight away.
              </p>

              {error !== null && (
                <Callout variant="danger" className="mt-4">
                  {error}
                </Callout>
              )}

              <Field
                label="New password"
                htmlFor="af-invite-password"
                className="mt-6"
                hint={`At least ${MIN_LENGTH} characters.`}
                error={tooShort ? `Use at least ${MIN_LENGTH} characters` : undefined}
              >
                <div className="relative">
                  <Input
                    id="af-invite-password"
                    type={revealed ? 'text' : 'password'}
                    size="lg"
                    autoComplete="new-password"
                    autoFocus
                    value={password}
                    iconLeft={<Lock />}
                    containerClassName="pr-8"
                    invalid={tooShort}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <button
                    type="button"
                    aria-label={revealed ? 'Hide password' : 'Show password'}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-secondary"
                    onClick={() => setRevealed((v) => !v)}
                  >
                    {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </Field>

              <Field
                label="Confirm password"
                htmlFor="af-invite-confirm"
                className="mt-4"
                error={mismatch ? 'These do not match' : undefined}
              >
                <Input
                  id="af-invite-confirm"
                  type={revealed ? 'text' : 'password'}
                  size="lg"
                  autoComplete="new-password"
                  value={confirm}
                  iconLeft={<Lock />}
                  invalid={mismatch}
                  onChange={(event) => setConfirm(event.target.value)}
                />
              </Field>

              <Button type="submit" className="mt-6 w-full" disabled={!canSubmit}>
                {submitting ? 'Setting your password…' : 'Set password and sign in'}
              </Button>
            </form>
          )}
        </div>
      </main>
    </div>
  );
}

export default InvitePage;
